import { useCallback, useState } from 'react';

import { useLoading } from '@/contextProviders/loadingProvider';
import { useFetchWithAuth } from '@/queries/useFetchWithAuth';

export const useGetQuery = <T>(mapFunction: (rawData: any) => T, hideLoader = false) => {
  const [data, setData] = useState<T | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { setLoading } = useLoading();
  const [hasBeenCalled, setHasBeenCalled] = useState(false);
  const { fetchWithAuth } = useFetchWithAuth();

  const fetchData = useCallback(
    async (query: string) => {
      try {
        if (!hasBeenCalled) {
          setHasBeenCalled(true);
        }
        setErrorMessage(null);
        hideLoader ? setLoading(false) : setLoading(true);
        const response = await fetchWithAuth(query);
        if (!response.ok) {
          setErrorMessage(`Error: ${response.status} ${response.statusText}`);
          return;
        }
        const json = await response.json();
        setData(mapFunction(json));
      } catch (error: unknown | Error) {
        if (error instanceof Error) {
          const localErrorMessage = error.message;
          setErrorMessage(localErrorMessage);
          setData(null);
          console.log(localErrorMessage);
        } else {
          console.log(error);
        }
      } finally {
        setLoading(false);
      }
    },
    [fetchWithAuth, hideLoader, mapFunction, setLoading],
  );

  const clearData = useCallback(() => {
    setData(null);
  }, []);

  return { data, errorMessage, fetchData, clearData, hasBeenCalled };
};
